// 14-day deadline strip fed by GET /api/firm/deadlines - the same feed
// /calendar reads, keyed by the shared day-key contract, so the two
// screens can never disagree on a date.
//
// Each day-column with work is clickable: exactly one matter due jumps
// straight to it, anything else falls back to the cmd-K palette. Zero
// deadlines renders non-interactive - no fake affordance.

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { SEP } from "../chrome/SepList";
import { useFirmAgenda } from "../../hooks/useFirmAgenda";
import { itemDayKey, localKey, type AgendaItem } from "../../api/firmDeadlines";

const RADAR_DAYS = 14;
const DOW_LABEL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface RadarDay {
  key: string;
  dow: string;
  lbl: string;
  today: boolean;
  count: number;
  crit: number;
  soon: number;
  ok: number;
  eIds: string[];
}

// Severity comes from the server (critical <=3d, urgent <=7d, then
// caution/safe) and maps onto the legend: critical / this week / upcoming.
function buildRadar(items: AgendaItem[]): RadarDay[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const out: RadarDay[] = [];
  for (let i = 0; i < RADAR_DAYS; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    out.push({
      key: localKey(d),
      dow: DOW_LABEL[d.getDay()],
      lbl: String(d.getDate()).padStart(2, "0"),
      today: i === 0,
      count: 0,
      crit: 0,
      soon: 0,
      ok: 0,
      eIds: [],
    });
  }
  const byKey = new Map(out.map((o) => [o.key, o]));
  for (const it of items) {
    const day = byKey.get(itemDayKey(it.dueDate, it.allDay));
    if (!day) continue; // beyond the strip - the feed spans 90 days
    day.count++;
    if (it.severity === "critical") day.crit++;
    else if (it.severity === "urgent") day.soon++;
    else day.ok++;
    if (it.eId && !day.eIds.includes(it.eId)) day.eIds.push(it.eId);
  }
  return out;
}

interface RadarProps {
  onOpenSearch?: () => void;
}

function RadarHead() {
  return (
    <div className="cc-card-head">
      <div className="cc-card-title">Deadline radar</div>
      <div className="cc-card-sub">
        {SEP} next 14 days {SEP} all practice areas
      </div>
      <div className="actions">
        <div className="cc-radar-legend">
          <span>
            <span className="sw crit" />
            critical
          </span>
          <span>
            <span className="sw soon" />
            this week
          </span>
          <span>
            <span className="sw ok" />
            upcoming
          </span>
        </div>
      </div>
    </div>
  );
}

export function DeadlineRadar({ onOpenSearch }: RadarProps = {}) {
  const navigate = useNavigate();
  const agenda = useFirmAgenda();
  const items = agenda.data?.deadlines;
  const radar = useMemo(() => buildRadar(items ?? []), [items]);
  const max = Math.max(1, ...radar.map((d) => d.count));
  const totals = radar.reduce(
    (acc, d) => ({
      crit: acc.crit + d.crit,
      soon: acc.soon + d.soon,
      ok: acc.ok + d.ok,
      total: acc.total + d.count,
    }),
    { crit: 0, soon: 0, ok: 0, total: 0 },
  );

  if (agenda.isLoading) {
    return (
      <div className="cc-card">
        <RadarHead />
        <SkeletonGroup>
          <Skeleton height={140} />
          <Skeleton width={280} />
        </SkeletonGroup>
      </div>
    );
  }

  if (agenda.isError || !items) {
    return (
      <div className="cc-card">
        <RadarHead />
        <div style={{ padding: "18px 0", color: "var(--warn-600)", fontSize: 13 }}>
          Could not reach the server - the radar is unavailable.
        </div>
      </div>
    );
  }

  return (
    <div className="cc-card">
      <RadarHead />
      <div className="cc-radar">
        <div className="cc-radar-strip">
          {radar.map((d) => {
            const cls = d.crit ? "crit" : d.soon ? "soon" : "ok";
            const heights: Array<{ kind: "crit" | "soon" | "ok"; v: number }> = [];
            if (d.crit) heights.push({ kind: "crit", v: d.crit });
            if (d.soon) heights.push({ kind: "soon", v: d.soon });
            if (d.ok) heights.push({ kind: "ok", v: d.ok });
            // One matter on this day = direct deep-link. Anything else with
            // work = open the palette so the user can pick.
            const onDayClick = () => {
              if (d.eIds.length === 1) navigate(`/matter/${d.eIds[0]}`);
              else onOpenSearch?.();
            };
            const interactive = d.count > 0;
            const titleText = interactive
              ? d.eIds.length === 1
                ? `Open matter ${d.eIds[0]}`
                : `${d.count} deadline${d.count === 1 ? "" : "s"} - open search to pick`
              : "No deadlines";
            return (
              <div
                key={d.key}
                role={interactive ? "button" : undefined}
                tabIndex={interactive ? 0 : -1}
                title={titleText}
                aria-label={titleText}
                className={"cc-radar-day " + cls + (d.today ? " today" : "")}
                style={interactive ? { cursor: "pointer" } : undefined}
                onClick={interactive ? onDayClick : undefined}
                onKeyDown={
                  interactive
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onDayClick();
                        }
                      }
                    : undefined
                }
              >
                <div className="bars">
                  {heights.map((h, j) => (
                    <div
                      key={j}
                      className={"bar " + h.kind}
                      style={{ height: `${(h.v / max) * 100}%` }}
                    />
                  ))}
                  {!d.count && (
                    <div style={{ height: 4, background: "var(--line-2)", borderRadius: 3 }} />
                  )}
                </div>
                <div className="dow">{d.dow}</div>
                <div className="lbl">{d.lbl}</div>
              </div>
            );
          })}
        </div>
        <div className="cc-radar-meta">
          <div className="cc-radar-meta-item">
            <div className="v crit">{totals.crit}</div>
            <div className="l">Critical</div>
          </div>
          <div className="cc-radar-meta-item">
            <div className="v soon">{totals.soon}</div>
            <div className="l">This week</div>
          </div>
          <div className="cc-radar-meta-item">
            <div className="v">{totals.ok}</div>
            <div className="l">Upcoming</div>
          </div>
          <div className="cc-radar-meta-item">
            <div className="v">{totals.total}</div>
            <div className="l">14-day total</div>
          </div>
        </div>
      </div>
    </div>
  );
}
